import { type FC, Fragment, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { GripHorizontal, Pencil, Save, Trash2Icon } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { capitalize } from 'lodash';
import { z } from 'zod';
import { renderField } from '@/components/inspector/entity-fields/form/field-components';
import { OptionAction } from '@/components/inspector/entity-fields/form/option-action';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { deleteWorkflowStatus, updateWorkflow } from '@/services/workflow.service';
import { StatusColorSwatch } from '@/components/inspector/status-color-swatch';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { ModalId, useOpenModal } from '@/hooks/data/modal/useModalStore';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { TruncatedText } from '@/components/ui/truncated-text';
import { useWorkflow } from '@/hooks/workflow/useWorkflow';
import { type WorkflowStatuses } from '@/types/workflow';
import { Separator } from '@/components/ui/separator';
import { ToastVariant } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { error as errorLog } from '@/utilities/log';
import { FormLabel } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { QueryKey } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

type OptionItemProps = {
  options: Record<string, WorkflowStatuses>;
  optionKey: string;
  handleRemoveOption: (optionKey: string) => void;
  workflowId?: string;
  setOptions: (value: SetStateAction<Record<string, WorkflowStatuses>>) => void;
};

export const OptionItem: FC<OptionItemProps> = ({ options, optionKey, handleRemoveOption, workflowId, setOptions }) => {
  const openModal = useOpenModal();
  const tCommon = useTranslations('common');

  return (
    <div key={optionKey} className="flex h-12 flex-col justify-center gap-2 px-3">
      <div className="flex w-full items-center gap-2">
        <div className="flex grow items-center gap-2">
          <GripHorizontal className="icon-5 min-w-5 text-[#333333]" />

          {options[optionKey].color && <StatusColorSwatch color={options[optionKey].color} />}
          <div>
            <p title={options[optionKey].name} className="line-clamp-1 flex-1 text-left text-sm break-all">
              {options[optionKey].name}
            </p>
            {options[optionKey].description && (
              <p className=" line-clamp-1 flex-1 text-left text-xs break-all text-neutral-500">
                {options[optionKey].description}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-1">
          <OptionAction
            onClick={() => {
              // OptionItem is shared by draft workflow creation and saved workflow editing.
              // Use the inspector modal id when workflowId exists so close/back behavior targets the saved workflow flow.
              openModal(workflowId ? ModalId.CreateWorkflowStatusInspector : ModalId.CreateWorkflowStatus, {
                workflowId: workflowId,
                statusId: options[optionKey].id,
                name: options[optionKey].name,
                description: options[optionKey].description,
                color: options[optionKey].color,
                action: 'updateWorkflowStatus',
                statuses: options,
                setStatuses: setOptions,
              });
            }}
            icon={Pencil}
            data-cy="option-action-button-workflow-update-status"
            tooltip={tCommon('edit')}
          />
          <OptionAction
            onClick={() => handleRemoveOption(optionKey)}
            icon={Trash2Icon}
            data-cy="option-action-button-workflow-delete-status"
            variant="destructive"
            tooltip={tCommon('delete')}
          />
        </div>
      </div>
    </div>
  );
};

type WorkflowFieldData = {
  name?: string;
  description?: string;
  id: string;
  statuses?: Record<string, WorkflowStatuses>;
};

export const WorkflowFields: FC<{ workflowId: string }> = ({ workflowId }) => {
  const openModal = useOpenModal();

  const tCommon = useTranslations('common');
  const tDialogs = useTranslations('dialogs');
  const tWorkflowFields = useTranslations('inspector.workflowFields');
  const tValidation = useTranslations('validation');
  const tToasts = useTranslations('toasts');
  const tErrors = useTranslations('errors');

  const { toast } = useToast();
  const {
    workflow: fieldData,
    workflowIsFetching,
    workflowIsPending,
  } = useWorkflow(workflowId) as {
    workflow: WorkflowFieldData;
    workflowIsFetching: boolean;
    workflowIsPending: boolean;
  };

  const [options, setOptions] = useState<Record<string, WorkflowStatuses>>(
    fieldData?.statuses
      ? Object.fromEntries(
          Object.entries(fieldData.statuses).map(([key, value]) => [
            key,
            { id: value.id, name: value.name, color: value.color, description: value.description },
          ]),
        )
      : {},
  );

  // Create a dynamic form schema based on the fieldData
  const createFormSchema = (data: WorkflowFieldData) => {
    const schema: Record<string, z.ZodType<any>> = {};

    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'name':
          schema[key] = z
            .string()
            .min(1, { message: tValidation('fieldRequired', { field: `${capitalize(key)} field` }) })
            .max(255, { message: tValidation('fieldMaxLength', { field: `${capitalize(key)} field`, max: 255 }) });
          break;
        case 'description':
          schema[key] = z
            .string({
              error: (issue) => {
                if (issue.input === undefined) {
                  return tValidation('fieldMustBeString', { field: tValidation('fields.descriptionField') });
                }
              },
            })
            .nullable();
          break;
        default:
          break;
      }
    });

    return z.object(schema);
  };

  const formSchema = fieldData ? createFormSchema(fieldData) : z.object({});

  // Only real form fields may go into the reactive `values` prop. Including
  // anything else (statuses, id) makes react-hook-form treat a status-only
  // refetch as a change and reset the whole form, wiping unsaved edits (DM-1113).
  const defaultValues = useMemo(
    () => ({ name: fieldData?.name ?? '', description: fieldData?.description ?? '' }),
    [fieldData],
  );

  const form = useForm({
    resolver: zodResolver(createFormSchema(fieldData || { id: workflowId, name: '', description: '' })),
    defaultValues,
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
  });

  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  const queryClient = useQueryClient();

  const deleteWorkflowStatusWithAuth = useAuthenticatedQueryFn(deleteWorkflowStatus);

  const deleteWorkflowStatusMutation = useMutation({
    mutationFn: async ({ workflowId: targetWorkflowId, statusId }: { workflowId: string; statusId: string }) => {
      await deleteWorkflowStatusWithAuth({
        workflowId: targetWorkflowId,
        statusId,
      });
    },
    onSuccess: (_data, { workflowId: deletedStatusWorkflowId }) => {
      toast({
        title: tToasts('deleteWorkflowStatusMutation.title'),
        description: tToasts('deleteWorkflowStatusMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.WorkflowList] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Workflow, deletedStatusWorkflowId] });
    },
    onError: (err) => {
      errorLog('Action failed:', err);

      toast({
        variant: ToastVariant.DESTRUCTIVE,
        title: tCommon('error'),
        description: err.message ?? tErrors('toastDefaultDescription'),
      });
    },
  });

  const handleRemoveOption = (key: string) => {
    openModal(ModalId.DeleteConfirmationAlertDialog, {
      'content': (
        <>
          {tDialogs('deleteWorkflowStatusPrefix')}
          <div className="flex items-center">
            <StatusColorSwatch color={options[key].color} className="mr-1" />
            <TruncatedText text={String(options[key].name)} className="min-w-0 font-bold" />
          </div>
        </>
      ),
      'onClick': () => {
        if (workflowId && options[key].id) {
          deleteWorkflowStatusMutation.mutate({
            workflowId: workflowId,
            statusId: options[key].id,
          });
        }
      },
      'data-cy': 'delete-workflow-status',
    });
  };

  const handleAddNewOption = () => {
    openModal(ModalId.CreateWorkflowStatusInspector, {
      workflowId: workflowId,
      name: '',
      description: '',
      color: '#1456ff',
      action: 'createWorkflowStatus',
      statuses: options,
      setStatuses: setOptions,
    });
  };

  const prevStatusesString = useRef<string>('');

  // Reset form values when fieldData changes
  useEffect(() => {
    const currentStatuses = fieldData?.statuses;
    const currentString = currentStatuses ? JSON.stringify(currentStatuses) : '';

    if (currentString !== prevStatusesString.current) {
      setOptions(
        fieldData?.statuses
          ? Object.fromEntries(
              Object.entries(fieldData.statuses).map(([key, value]) => [
                key,
                { id: value.id, name: value.name, color: value.color, description: value.description },
              ]),
            )
          : {},
      );
    }
    prevStatusesString.current = currentString;
  }, [fieldData]);

  const updateWorkflowWithAuth = useAuthenticatedQueryFn(updateWorkflow);

  const updateWorkflowMutation = useMutation({
    mutationFn: updateWorkflowWithAuth,
    onSuccess: async (data) => {
      toast({
        title: tToasts('updateWorkflowMutation.title'),
        description: tToasts('updateWorkflowMutation.description'),
      });

      // Mark the saved values as pristine so keepDirtyValues doesn't pin them
      // against future server-side changes. The hook-level `resetOptions:
      // { keepDirtyValues: true }` (DM-1113) is merged into every form.reset()
      // call, including this one — without the explicit override below, the
      // just-saved field would stay marked dirty and keep ignoring later
      // server-side changes.
      form.reset({ name: data.name, description: data.description ?? '' }, { keepDirtyValues: false });

      await queryClient.invalidateQueries({ queryKey: [QueryKey.WorkflowList] });
      await queryClient.invalidateQueries({ queryKey: [QueryKey.Workflow, workflowId] });
    },
    onError: (err: BackendError) => {
      handleError(err);
      errorLog(err);
    },
  });

  function onSubmit(values: z.infer<ReturnType<typeof createFormSchema>>) {
    // Statuses are persisted through their own endpoints (the backend ignores
    // a `statuses` field on the workflow PATCH) — submit only the form fields.
    updateWorkflowMutation.mutate({
      id: fieldData.id,
      body: values,
    });
  }

  const isLoading = updateWorkflowMutation.isPending;

  return (
    <>
      {workflowIsPending ? (
        <div className="flex flex-col space-y-6 p-4">
          {Array.from(Array(3)).map((_, index) => (
            <Fragment key={`skeleton-${index}`}>
              <div className="flex flex-col space-y-3">
                <Skeleton className={cn('h-5 w-1/3', { 'w-2/5': index === 0 })} />
                <Skeleton className="h-8 w-full" />
                <Skeleton className={cn('h-5 w-1/3', { 'w-2/3': index === 1 })} />
              </div>
              {index !== 2 && <Separator className="block" />}
            </Fragment>
          ))}
        </div>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative flex flex-col space-y-6 p-4">
            {Object.keys(fieldData).map((key) => {
              const validation = (formSchema.shape as Record<string, z.ZodType<any>>)[key];

              switch (key) {
                case 'name':
                  return renderField(form, {
                    'name': 'Workflow Name',
                    'value': (form.watch('name') as string) || '',
                    'type': 'string',
                    'slug': key,
                    'disabled': workflowIsFetching || isLoading,
                    validation,
                    'data-cy': 'workflow-name-field',
                    'isLoading': workflowIsFetching,
                  });
                case 'description':
                  return renderField(form, {
                    'name': 'Workflow Description',
                    'value': (form.watch('description') as string) || '',
                    'type': 'text',
                    'slug': key,
                    'disabled': workflowIsFetching || isLoading,
                    validation,
                    'data-cy': 'workflow-description-field',
                    'isLoading': workflowIsFetching,
                  });
                case 'statuses':
                  return (
                    <div key={key} className="flex flex-col gap-4">
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-start gap-2">
                          <FormLabel className="leading-4">{tWorkflowFields('statuses')}</FormLabel>
                        </div>
                        <div className="relative">
                          <Options
                            handleAddNewOption={handleAddNewOption}
                            disabled={workflowIsFetching || isLoading}
                            isLoading={workflowIsFetching}
                          >
                            {Object.keys(options).map((optionKey) => (
                              <OptionItem
                                key={optionKey}
                                optionKey={optionKey}
                                options={options}
                                handleRemoveOption={handleRemoveOption}
                                workflowId={workflowId}
                                setOptions={setOptions}
                              />
                            ))}
                          </Options>
                          <Separator className="mt-6 hidden" />
                        </div>
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            })}

            <Button
              type="submit"
              loading={isLoading}
              disabled={workflowIsFetching || !form.formState.isDirty}
              variant="navigation-action-primary"
              className="w-full justify-center focus-visible:ring-offset-ui-panel"
              data-cy="workflow-save-button"
              iconClassName="icon-4!"
              icon={Save}
            >
              {isLoading ? tCommon('saving') : tCommon('save')}
            </Button>
          </form>
        </FormProvider>
      )}
    </>
  );
};
